import React, { useEffect, useState } from 'react';
import { Activity, Crown, LogOut, PanelLeft, PanelLeftClose, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import LanguageSelector from '../LanguageSelector';
import MobileNav from '../MobileNav';
import { NAV_ICONS } from './adminNavIcons';
import { getAdminTabPageMeta } from './adminTabMeta';
import { useWindowSize } from '../../hooks/useWindowSize';
import { BREAKPOINTS } from '../../utils/responsive';
import { useLanguage } from '../../context/LanguageContext';
import PortalAvatar from '../PortalAvatar';
import { loadProfilePhoto } from '../../utils/profilePhotoStorage';

function AccountStatusPill({ status }) {
  const key = String(status || 'active').toLowerCase();
  const tones = {
    active: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', label: '● Active' },
    pending: { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', label: '● Pending' },
    rejected: { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', label: '● Rejected' },
    inactive: { background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid rgba(156,163,175,0.3)', label: '● Inactive' },
  };
  const tone = tones[key] || tones.active;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        marginLeft: '8px',
        background: tone.background,
        color: tone.color,
        border: tone.border,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </span>
  );
}

function NavIcon({ id, active }) {
  const Icon = NAV_ICONS[id] || NAV_ICONS.overview;
  return (
    <Icon
      className={cn('h-[18px] w-[18px] shrink-0 transition-colors', active ? 'text-de-gold' : 'text-de-muted group-hover:text-slate-300')}
      strokeWidth={active ? 2.5 : 2}
    />
  );
}

function DeSidebarBrand({ compact = false }) {
  const markSize = compact ? 'h-10 w-10 text-xs' : 'h-12 w-12 text-sm';
  return (
    <div className={cn('de-brand-block mx-2', compact ? 'mb-3' : 'mb-5')}>
      <div className={cn('flex flex-col items-center text-center', compact ? 'px-2 py-3' : 'px-3 py-4')}>
        <div className={cn('de-brand-mark mb-2.5', markSize)}>
          <span className="de-brand-mark-ring" aria-hidden />
          <span className="relative z-[1] tracking-tight">DE</span>
        </div>
        <p className="m-0 w-full whitespace-normal text-[12px] font-extrabold leading-snug tracking-tight de-shimmer-text xl:text-[13px]">
          Development Express
        </p>
        <span
          className="my-2 block h-px w-full"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.55), transparent)' }}
          aria-hidden
        />
        <span className="de-brand-since-pill">
          <Sparkles className="h-2.5 w-2.5 shrink-0 opacity-90" aria-hidden />
          Since 2011
        </span>
      </div>
    </div>
  );
}

function SidebarPanel({
  navItems,
  activeTab,
  onNavSelect,
  onLogout,
  pendingCount,
  sidebarOpen,
  sidebarOverlay,
  onSidebarClose,
}) {
  const showLabels = sidebarOpen || !sidebarOverlay;

  return (
    <>
      {sidebarOpen ? (
        <div className="de-sidebar-fade shrink-0 opacity-100">
          <div className="flex items-start justify-between gap-2 px-1">
            <div className="min-w-0 flex-1">
              <DeSidebarBrand compact={sidebarOverlay} />
            </div>
            {onSidebarClose ? (
              <button
                type="button"
                onClick={onSidebarClose}
                className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-de-gold/35 bg-de-gold/10 text-de-gold transition-colors hover:border-de-gold/55 hover:bg-de-gold/20"
                aria-label="Close sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        !sidebarOverlay && (
          <div className="de-sidebar-fade mx-2 mb-4 flex shrink-0 justify-center opacity-100">
            <div className="de-brand-mark h-11 w-11 text-xs">
              <span className="de-brand-mark-ring" aria-hidden />
              <span className="relative z-[1] tracking-tight">DE</span>
            </div>
          </div>
        )
      )}
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden px-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {navItems.map((item) => {
          const active = activeTab === item.id;
          const badge = item.id === 'approvals' && pendingCount > 0 ? pendingCount : item.badge;
          return (
            <button
              key={item.id}
              type="button"
              title={item.label}
              onClick={() => onNavSelect(item.id)}
              className={cn(
                'group relative flex w-full items-center rounded-xl border py-2.5 text-left transition-all duration-300',
                showLabels ? 'gap-2.5 px-2.5' : 'justify-center px-0',
                active
                  ? 'border-de-gold/60 bg-gradient-to-r from-de-gold/30 to-de-gold/5 de-glow-gold'
                  : 'border-transparent bg-transparent hover:border-de-gold/25 hover:bg-de-gold/10',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                  active ? 'border-de-gold/40 bg-de-gold/15' : 'border-de-gold/10 bg-black/25',
                )}
              >
                <NavIcon id={item.id} active={active} />
              </span>
              <span
                className={cn(
                  'de-sidebar-fade min-w-0 flex-1 truncate text-xs font-semibold',
                  active ? 'text-de-gold' : 'text-de-muted group-hover:text-slate-200',
                  showLabels ? 'max-w-[12rem] opacity-100' : 'pointer-events-none max-w-0 opacity-0',
                )}
              >
                {item.label}
              </span>
              {badge && showLabels ? (
                <span className="de-sidebar-fade flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-de-accent px-1 text-[9px] font-bold text-white opacity-100">
                  {badge}
                </span>
              ) : null}
              {badge && !showLabels ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-de-accent px-0.5 text-[8px] font-bold text-white">
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
      <div className="mt-2 shrink-0 border-t border-de-gold/15 px-2 pt-3">
        <Button
          type="button"
          variant="outline"
          title="Logout"
          className={cn(
            'h-10 w-full border-de-accent/30 text-de-accent hover:bg-de-accent/15',
            showLabels ? 'justify-start gap-2' : 'justify-center px-0',
          )}
          onClick={onLogout}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          <span
            className={cn(
              'de-sidebar-fade text-xs font-semibold',
              showLabels ? 'max-w-[8rem] opacity-100' : 'max-w-0 overflow-hidden opacity-0',
            )}
          >
            Logout
          </span>
        </Button>
      </div>
    </>
  );
}

export default function AdminCommandShell({
  navItems,
  activeTab,
  setActiveTab,
  useMobileNav,
  onLogout,
  activeLabel,
  pendingCount = 0,
  children,
}) {
  const { t } = useLanguage();
  const pageMeta = getAdminTabPageMeta(activeTab, activeLabel, t);
  const { isTablet, width } = useWindowSize();
  const sidebarOverlay = isTablet;
  const compactMain = width < BREAKPOINTS.layoutDesktop;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [adminPhoto, setAdminPhoto] = useState('');

  let adminId = null;
  let adminName = 'Admin';
  let adminStatus = 'active';
  try {
    const adminUser = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    adminId = adminUser?.id || null;
    adminName = adminUser?.name || adminUser?.email || 'Admin';
    adminStatus = adminUser?.status || 'active';
  } catch {
    /* ignore */
  }

  useEffect(() => {
    setSidebarOpen(!sidebarOverlay);
  }, [sidebarOverlay]);

  useEffect(() => {
    setAdminPhoto(loadProfilePhoto(adminId, 'admin') || '');
  }, [adminId]);

  const handleNavSelect = (tabId) => {
    setActiveTab(tabId);
    if (sidebarOverlay) setSidebarOpen(false);
  };

  const pageHero = (
    <div className="de-ultra-hero mb-3 w-full min-w-0 px-4 py-4 sm:mb-0 sm:px-5 sm:py-5">
      <p className="mb-1.5 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-de-gold/90">
        <Activity className="h-3.5 w-3.5 shrink-0" />
        <span className="de-shimmer-text normal-case tracking-tight">Development Express</span>
      </p>
      <h1 className="m-0 break-words text-xl font-black leading-tight tracking-tight sm:text-3xl">
        <span className="de-shimmer-text">{pageMeta.title}</span>
      </h1>
      <p className="mt-1.5 max-w-xl break-words text-xs leading-snug text-de-muted sm:text-sm">{pageMeta.subtitle}</p>
    </div>
  );

  if (useMobileNav) {
    return (
      <div className="admin-command admin-mobile-shell relative min-h-screen max-w-[100vw] overflow-x-hidden bg-[#0D1B2A] text-white">
        <div className="de-mesh-bg pointer-events-none fixed inset-0 z-0" aria-hidden />
        <MobileNav
          navItems={navItems.map((item) => ({
            ...item,
            icon: item.Icon ? <item.Icon className="h-5 w-5 text-de-gold" /> : item.icon,
          }))}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          bottomPrimaryCount={5}
          drawerMode="full"
          drawerSectionTitle="All sections"
          title="Development Express"
          subtitle="Since 2011 · Admin"
          hideBottomNav
          bottomContent={
            <Button type="button" variant="outline" className="w-full border-de-accent/40 text-de-accent" onClick={onLogout}>
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          }
        />
        <main className="admin-mobile-main relative z-10 box-border w-full min-w-0 max-w-[100vw] overflow-x-hidden px-3 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pt-[calc(4.25rem+env(safe-area-inset-top,0px))]">
          {pageHero}
          <div className="w-full min-w-0 max-w-full space-y-4">{children}</div>
        </main>
      </div>
    );
  }

  const sidebarWidthClass = sidebarOpen
    ? 'w-[15.5rem] shadow-2xl xl:w-[16.5rem]'
    : sidebarOverlay
      ? 'w-[15.5rem]'
      : 'w-[4.5rem] shadow-xl shadow-black/40';

  return (
    <div className="admin-command relative flex h-[100svh] max-h-[100svh] overflow-hidden bg-[#0D1B2A] text-white">
      <div className="de-mesh-bg pointer-events-none fixed inset-0 z-0" aria-hidden />
      <div className="pointer-events-none fixed -left-40 top-0 z-0 h-[32rem] w-[32rem] rounded-full bg-de-gold/12 blur-[140px]" aria-hidden />
      <div className="pointer-events-none fixed -right-32 bottom-0 z-0 h-80 w-80 rounded-full bg-de-gold/6 blur-[100px]" aria-hidden />

      {sidebarOverlay && sidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-[1px]"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      {!sidebarOpen ? (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="fixed left-3 top-[calc(12px+env(safe-area-inset-top,0px))] z-50 flex h-9 w-9 items-center justify-center rounded-lg border border-de-gold/35 bg-de-gold/10 text-de-gold transition-colors hover:border-de-gold/55 hover:bg-de-gold/20"
          aria-label="Open sidebar"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      ) : null}

      {sidebarOverlay ? (
        <aside
          className={cn(
            'de-sidebar-shell fixed left-0 top-0 z-40 flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden border-r border-de-gold/25 bg-gradient-to-b from-[#0f2040]/98 to-[#060e1c]/98 py-4 backdrop-blur-xl transition-transform duration-300',
            sidebarWidthClass,
            sidebarOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none',
          )}
        >
          <SidebarPanel
            navItems={navItems}
            activeTab={activeTab}
            onNavSelect={handleNavSelect}
            onLogout={onLogout}
            pendingCount={pendingCount}
            sidebarOpen={sidebarOpen}
            sidebarOverlay={sidebarOverlay}
            onSidebarClose={() => setSidebarOpen(false)}
          />
        </aside>
      ) : sidebarOpen ? (
        <aside
          className={cn(
            'de-sidebar-shell sticky top-0 z-20 flex h-[100dvh] max-h-[100dvh] shrink-0 flex-col self-start overflow-hidden border-r border-de-gold/25 bg-gradient-to-b from-[#0f2040]/98 to-[#060e1c]/98 py-4 backdrop-blur-xl',
            sidebarWidthClass,
          )}
        >
          <SidebarPanel
            navItems={navItems}
            activeTab={activeTab}
            onNavSelect={handleNavSelect}
            onLogout={onLogout}
            pendingCount={pendingCount}
            sidebarOpen={sidebarOpen}
            sidebarOverlay={sidebarOverlay}
            onSidebarClose={() => setSidebarOpen(false)}
          />
        </aside>
      ) : null}

      <div className={cn('relative z-10 flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden', !sidebarOpen && 'pl-14')}>
        <header className="sticky top-0 z-30 shrink-0 border-b border-de-gold/25 bg-[#0a1628]/90 px-4 py-2.5 backdrop-blur-2xl sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h2 className="m-0 truncate text-base font-bold text-de-gold sm:text-lg">{pageMeta.title}</h2>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-full border border-de-gold/25 bg-de-gold/5 px-3 py-1.5">
              <PortalAvatar userId={adminId} name={adminName} photoUrl={adminPhoto} size={32} />
              <Crown className="h-4 w-4 shrink-0 text-de-gold" />
              <span className="truncate text-xs font-semibold text-de-gold">{adminName}</span>
              <AccountStatusPill status={adminStatus} />
              <LanguageSelector compact />
            </div>
          </div>
        </header>

        <div className={cn('mt-4 shrink-0', compactMain ? 'mx-2' : 'mx-4 sm:mx-6')}>
          <div className="de-ultra-hero relative px-4 py-5 sm:px-8 sm:py-7">
            <div className="relative flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="mb-2 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-de-gold/90">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span className="de-shimmer-text normal-case tracking-tight">Development Express</span>
                  <span className="de-brand-since-pill normal-case tracking-[0.18em]">Since 2011</span>
                </p>
                <h1 className="m-0 break-words text-2xl font-black tracking-tight sm:text-4xl">
                  <span className="de-shimmer-text">{pageMeta.title}</span>
                </h1>
                <p className="mt-2 max-w-xl text-sm text-de-muted">{pageMeta.subtitle}</p>
              </div>
              <div className="shrink-0 rounded-xl border border-de-gold/20 bg-black/30 px-4 py-3 text-right">
                <p className="m-0 text-[10px] uppercase tracking-wider text-de-muted">Today</p>
                <p className="m-0 text-sm font-semibold text-slate-200">
                  {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <main
          className={cn(
            'min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain py-4 sm:py-6',
            compactMain ? 'px-2 sm:px-3' : 'px-4 sm:px-6',
          )}
        >
          <div className="w-full min-w-0 max-w-none space-y-4">{children}</div>
        </main>
      </div>
    </div>
  );
}
